import type { Callables } from '../../services/types.js';
export interface PendingDraftRequest {
  request: Parameters<Callables['createCreation']>[0] & { requestId: string };
  refUrls: string[];
  acceptedCreationId?: string;
  acceptedSettingsRev?: number;
}
const key = (uid: string) => `djgg:creation-request:v1:${uid}`;
/** Recovery data is never authority. The server validates and owner-scopes it. */
export function readDraftRequest(uid: string): PendingDraftRequest | null {
  try {
    const raw = sessionStorage.getItem(key(uid));
    if (!uid || !raw || raw.length > 24000) return null;
    const value = JSON.parse(raw);
    const r = value?.request;
    if (!r || typeof r.requestId !== 'string' || !/^[a-zA-Z0-9_.-]{1,128}$/.test(r.requestId) ||
      typeof r.title !== 'string' || typeof r.concept !== 'string' ||
      typeof r.slug !== 'string' || typeof r.engine?.provider !== 'string' || typeof r.engine?.model !== 'string' ||
      !r.settings || typeof r.settings !== 'object' ||
      !Array.isArray(value.refUrls) || !value.refUrls.every((v: unknown) => typeof v === 'string') ||
      !Number.isFinite(value.savedAt) || Date.now() - value.savedAt > 86400000 || value.savedAt > Date.now()) return null;
    return { request: r, refUrls: value.refUrls,
      ...(Number.isSafeInteger(value.acceptedSettingsRev) && value.acceptedSettingsRev >= 0 ? { acceptedSettingsRev: value.acceptedSettingsRev } : {}),
      ...(typeof value.acceptedCreationId === 'string' ? { acceptedCreationId: value.acceptedCreationId } : {}) };
  } catch { return null; }
}
export function saveDraftRequest(uid: string, value: PendingDraftRequest): boolean {
  try {
    if (!uid) return false;
    sessionStorage.setItem(key(uid), JSON.stringify({ ...value, savedAt: Date.now() }));
    return true;
  } catch { return false; }
}
export function clearDraftRequest(uid: string, requestId: string): void {
  try {
    // Do not clear a newer request saved by another mounted editor.
    if (readDraftRequest(uid)?.request.requestId === requestId) sessionStorage.removeItem(key(uid));
  } catch { /* Mounted memory still provides same-request retry. */ }
}

/** Completing a separately resumed draft must clear ONLY its own receipt.
 * A lost response has no returned id, so match the server's owner-scoped hash.
 * This identity comparison grants no authority and makes no server request. */
export async function clearResumedDraftRequest(uid: string, creationId: string): Promise<void> {
  const pending = readDraftRequest(uid);
  if (!pending) return;
  let matches = pending.acceptedCreationId === creationId;
  if (!matches) {
    try {
      const bytes = new TextEncoder().encode(JSON.stringify([uid, pending.request.requestId]));
      const hash = await crypto.subtle.digest('SHA-256', bytes);
      matches = `draft-${Array.from(new Uint8Array(hash), b => b.toString(16).padStart(2, '0')).join('')}` === creationId;
    } catch { return; }
  }
  if (matches) clearDraftRequest(uid, pending.request.requestId);
}
